import config from '../config';

const coveo = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      /**
       * This is the API key that Coveo services use to authenticate requests.
       * It is a public API key with very limited privileges, meaning
       * that it can be exposed with no security risk.
       *
       * However, this should be changed in the future due to analytics
       * logging issues that result from using this authentication method.
       *
       * @see [Public API Key Authentication Scenario](https://docs.coveo.com/en/1369/build-a-search-ui/choose-and-implement-a-search-authentication-method#public-api-key-authentication-scenario)
       */
      publicApiKey: 'xx9a08cb02-81d6-46cf-ab78-75d208d45174',

      /**
       * The base url used for the search API.
       */
      baseUrl: {
        default:
          'https://deckersoutdoorcorporationnonproduction1wo0g0rc3.org.coveo.com/rest',
        prod: 'https://deckersoutdoorcorporationproductionkfw5awg9.org.coveo.com/rest'
      },

      /**
       * The base url used for the analytics API. It can have one of two endpoint addtions
       * click and search.
       * @see https://docs.coveo.com/en/1502/build-a-search-ui/log-search-events
       */
      baseAnalyticsUrl: {
        default:
          'https://deckersoutdoorcorporationnonproduction1wo0g0rc3.analytics.org.coveo.com/rest/v15/analytics',
        prod: 'https://deckersoutdoorcorporationproductionkfw5awg9.analytics.org.coveo.com/rest/v15/analytics'
      },

      /**
       * Unique identifier for the [Coveo Organization](https://docs.coveo.com/en/185/glossary/coveo-organization)
       * to use.
       *
       * A Coveo Organization is a single tenant instance that hosts
       * data and Coveo service configurations.
       */
      orgId: {
        default: 'deckersoutdoorcorporationnonproduction1wo0g0rc3',
        prod: 'deckersoutdoorcorporationproductionkfw5awg9'
      },

      /**
       * The Coveo [Search Hub](https://docs.coveo.com/en/1342/glossary/search-hub)
       * to use.
       *
       * A Search Hub is a query parameter whose value is a descriptive
       * name of the search interface from which the query originates.
       * This allow us to create an optimized query pipeline for each
       * specific search interface.
       */
      searchHub: 'search-sanuk-us-en',

      /**
       * The Coveo (https://docs.coveo.com/en/n8tg0567/analyze-usage-data/what-s-a-tracking-id)
       * to use.
       *
       * A tracking ID is a unique identifier that distinguishes and categorizes Coveo Usage Analytics (Coveo UA) data
       * by site or application. It links all events within a single experience, streamlining the analysis of data such
       * as user behavior and preferences.
       */
      trackingId: 'sanuk-us-en',

      /** The default [number of results](https://docs.coveo.com/en/1461/build-a-search-ui/query-parameters#RestQueryParameters-numberOfResults) to use for list queries. */
      defaultLimit: 5000,

      /**
       * The default [facet requests](https://docs.coveo.com/en/1461/build-a-search-ui/query-parameters#RestQueryParameters-facets) to include in list queries.
       * @see [Facet Request Schema](https://docs.coveo.com/en/1461/build-a-search-ui/query-parameters#RestQueryParameters-facets)
       *
       * @todo -- For the `numberOfValues` ideally we should have that more optimized. We use 100 to bring most number of filters as possible,
       * and not miss anything on the UI, but we could optimize that to a lower number per filter in the future.
       */
      defaultFacetRequests: {
        0: {
          facetId: '@headless_gender',
          field: 'headless_gender',
          numberOfValues: 100
        },
        1: {
          facetId: '@headless_color_bucket',
          field: 'headless_color_bucket',
          numberOfValues: 100
        },
        2: {
          facetId: '@headless_retail_price',
          field: 'headless_retail_price',
          type: 'numericalRange'
        },
        3: {
          facetId: '@headless_category',
          field: 'headless_category',
          type: 'hierarchical',
          numberOfValues: 100
        }
      }
    }
  },
  'UGG-US': {
    default: {
      publicApiKey: {
        default: 'xx35f3194a-ece6-4124-b1d2-471c6e71fdff',
        prod: 'xx491fdf4a-e0a9-4e22-b57d-62c69696e231'
      },
      trackingId: 'ugg-us-en',
      searchHub: 'search-ugg-us-en',
      defaultFacetRequests: {
        0: {
          facetId: '@deckers_shoe_type',
          field: 'deckers_shoe_type',
          numberOfValues: 20,
          type: 'specific'
        },
        1: {
          facetId: '@deckers_size',
          field: 'deckers_size',
          numberOfValues: 50,
          type: 'specific',
          sortCriteria: 'alphanumericNatural'
        },
        2: {
          facetId: '@deckers_color_bucket',
          field: 'deckers_color_bucket',
          numberOfValues: 20,
          type: 'specific'
        },
        3: {
          facetId: '@deckers_material',
          field: 'deckers_material',
          numberOfValues: 10,
          type: 'specific'
        },
        4: {
          facetId: '@deckers_price',
          field: 'deckers_price',
          type: 'numericalRange',
          generateAutomaticRanges: true,
          rangeAlgorithm: 'even'
        }
      }
    }
  },
  'HOKA-US': {
    default: {
      publicApiKey: {
        default: 'xx491fdf4a-e0a9-4e22-b57d-62c69696e231',
        prod: 'xxbf05b21f-c4aa-439f-82f1-c1f1820139f2'
      },
      trackingId: 'hoka-us-en',
      searchHub: 'search-hoka-us-en',
      defaultFacetRequests: {
        0: {
          facetId: '@deckers_gender',
          field: 'deckers_gender',
          numberOfValues: 20,
          type: 'specific'
        },
        1: {
          facetId: '@deckers_size',
          field: 'deckers_size',
          numberOfValues: 50,
          type: 'specific',
          sortCriteria: 'alphanumericNatural'
        },
        2: {
          facetId: '@deckers_width',
          field: 'deckers_width',
          numberOfValues: 10,
          type: 'specific'
        },
        3: {
          facetId: '@deckers_color_bucket',
          field: 'deckers_color_bucket',
          numberOfValues: 100,
          type: 'specific'
        },
        4: {
          facetId: '@deckers_shoe_surface',
          field: 'deckers_shoe_surface',
          numberOfValues: 20,
          type: 'specific'
        },
        5: {
          facetId: '@deckers_shoe_ride',
          field: 'deckers_shoe_ride',
          numberOfValues: 20,
          type: 'specific'
        },
        6: {
          facetId: '@deckers_shoe_stability',
          field: 'deckers_shoe_stability',
          numberOfValues: 20,
          type: 'specific'
        },
        7: {
          facetId: '@deckers_heel_toe_drop',
          field: 'deckers_heel_toe_drop',
          numberOfValues: 20,
          type: 'specific'
        },
        8: {
          facetId: '@deckers_price',
          field: 'deckers_price',
          type: 'numericalRange',
          generateAutomaticRanges: true,
          rangeAlgorithm: 'even'
        }
      }
    }
  },
  'TEVA-US': {
    default: {
      publicApiKey: {
        default: 'xx06b05634-4fbb-4c01-b8a5-44dad0efc0fd',
        prod: 'xxa3deb254-2074-4446-b5a2-80a0470dcd1c'
      },
      trackingId: 'teva-us-en',
      searchHub: 'search-teva-us-en',
      defaultFacetRequests: {
        0: {
          facetId: '@deckers_gender',
          field: 'deckers_gender',
          numberOfValues: 100,
          type: 'specific'
        },
        1: {
          facetId: '@deckers_size',
          numberOfValues: 100,
          field: 'deckers_size',
          type: 'specific'
        },
        2: {
          facetId: '@deckers_color_bucket',
          field: 'deckers_color_bucket',
          numberOfValues: 100,
          type: 'specific'
        },
        /**
         * This filter is related to the `Best For` filter in search pages.
         *
         * This filter in coveo was disabled due to facet configuration issues.
         * We are leaving it commented, in case we need to re-enable after the issue is resolved.
         *
         * See: https://deckersglobal.atlassian.net/browse/DECA-6012.
         */
        // 3: {
        //   facetId: '@deckers_shoe_surface',
        //   field: 'deckers_shoe_surface',
        //   numberOfValues: 100
        // },
        4: {
          facetId: '@deckers_shoe_type',
          field: 'deckers_shoe_type',
          numberOfValues: 100,
          type: 'specific'
        },
        5: {
          facetId: '@deckers_price',
          field: 'deckers_price',
          type: 'numericalRange'
        }
      }
    }
  }
});

export default coveo;
